import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';
import {
    SVGChevronLeft, SVGChevronRight, SVGCross,
    SVGPlane, SVGHotel, SVGSlidersHorizontal, SVGEdit,
} from '../assets/PackageSVG';
import styles from './SideBarRight.module.scss';
import { Dropdown } from './Dropdown';
import type {
    ActiveChipsProps,
    DestinationFilters,
    FlightFilters,
    HotelFilters,
    RoomFilters,
    SideBarRightProps,
} from '../types/filterSidebar';

/* ═══════════════════════════════════════════════════════
   Active Filter Chips
   ═══════════════════════════════════════════════════════ */
const ActiveChips: React.FC<ActiveChipsProps> = ({
    flightFilters, hotelFilters, phase, onClearFlight, onClearHotel,
}) => {
    const chips: { label: string; onClear: () => void }[] = [];

    if (phase === 'flights') {
        if (flightFilters.stops !== 'any')
            chips.push({
                label: flightFilters.stops === 'direct' ? 'Direct'
                    : flightFilters.stops === '1stop' ? '1 stop' : '2+ stops',
                onClear: () => onClearFlight('stops'),
            });
        if (flightFilters.sort !== 'best')
            chips.push({ label: `Sort: ${flightFilters.sort}`, onClear: () => onClearFlight('sort') });
        if (flightFilters.maxPrice)
            chips.push({ label: `≤ $${flightFilters.maxPrice}`, onClear: () => onClearFlight('maxPrice') });
        if (flightFilters.time !== 'any')
            chips.push({ label: flightFilters.time, onClear: () => onClearFlight('time') });
        flightFilters.airlines.forEach(a =>
            chips.push({ label: a, onClear: () => onClearFlight('airline', a) }));
    }

    if (phase === 'hotels') {
        hotelFilters.stars.forEach(s =>
            chips.push({ label: `${s}★`, onClear: () => onClearHotel('star', s) }));
        if (hotelFilters.maxPrice)
            chips.push({ label: `≤ $${hotelFilters.maxPrice}/night`, onClear: () => onClearHotel('maxPrice') });
        hotelFilters.amenities.forEach(a =>
            chips.push({ label: a, onClear: () => onClearHotel('amenity', a) }));
        hotelFilters.propertyType.forEach(t =>
            chips.push({ label: t, onClear: () => onClearHotel('propertyType', t) }));
        if (hotelFilters.guestRating > 0)
            chips.push({ label: `${hotelFilters.guestRating}+ rating`, onClear: () => onClearHotel('guestRating') });
    }

    if (!chips.length) return null;

    return (
        <div className={styles.fsActiveChips}>
            {chips.map((c, i) => (
                <span key={i} className={styles.fsActiveChip}>
                    {c.label}
                    <button onClick={c.onClear}><SVGCross width="10" height="10" /></button>
                </span>
            ))}
        </div>
    );
};

const DESTINATION_REGIONS = ['Europe', 'Asia', 'USA', 'Latin America', 'Africa', 'Oceania'];
const DESTINATION_CATEGORIES = ['beach', 'culture', 'adventure', 'food', 'budget', 'luxury', 'city', 'romance'];
const DESTINATION_MOODS = ['beach', 'wellness', 'nature', 'romance', 'nightlife', 'family', 'shopping'];
const ROOM_BED_TYPES = ['Any', 'King', 'Queen', 'Twin'];
const ROOM_AMENITIES = ['WiFi', 'Breakfast', 'Pool', 'Parking', 'Gym', 'Spa', 'Pet-friendly'];
const REGIONS = ['All', 'USA', 'Europe', 'Asia', 'Latin America', 'Africa', 'Oceania'];
const BUDGETS_LIST = ['Any', '$0-500', '$500-1K', '$1K-2.5K', '$2.5K-5K', '$5K+'];
const ACTIVITIES = ['All', 'Beach', 'Nature', 'Culture', 'Wellness', 'Food', 'Adventure', 'Romance'];

/* ═══════════════════════════════════════════════════════
   SideBarRight (FilterSidebar)
   ═══════════════════════════════════════════════════════ */
export const SideBarRight: React.FC<SideBarRightProps> = ({
    collapsed, onToggle,
    phase,
    initialSeenPhases,
    initialOpenPhase,
    flightFilters, onFlightFiltersChange,
    hotelFilters, onHotelFiltersChange,
    destinationFilters, onDestinationFiltersChange,
    roomFilters, onRoomFiltersChange,
    maxFlightPrice = 5000,
    maxHotelPrice = 2000,
    airlines = [],
    selectedFlight, selectedHotel,
    onRemoveFlight, onRemoveHotel,
    onClearAll,
    staticCheckIn,
    staticCheckOut,
    onStaticDatesChange,
}) => {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [region, setRegion] = useState('All');
    const [budget, setBudget] = useState('Any');
    const [activity, setActivity] = useState('All');

    const checkIn = staticCheckIn || '';
    const checkOut = staticCheckOut || '';

    const updateDates = (nextCheckIn: string, nextCheckOut: string) => {
        onStaticDatesChange?.({ checkIn: nextCheckIn, checkOut: nextCheckOut });
    };

    // Static summary card state
    const [editingField, setEditingField] = useState<'region' | 'dates' | 'party' | null>(null);
    const [partyRooms, setPartyRooms] = useState(1);
    const [partyAdults, setPartyAdults] = useState(1);
    const [partyChildren, setPartyChildren] = useState(0);

    const toggleEdit = (field: 'region' | 'dates' | 'party') =>
        setEditingField(prev => (prev === field ? null : field));

    const nightsCount = checkIn && checkOut
        ? Math.max(0, Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000))
        : 0;

    const partySummary = `${partyRooms} room${partyRooms !== 1 ? 's' : ''}, ${partyAdults} adult${partyAdults !== 1 ? 's' : ''}${partyChildren > 0 ? ` + ${partyChildren} child${partyChildren !== 1 ? 'ren' : ''}` : ''}`;

    const counter = (value: number, onChange: (v: number) => void, min = 0, max = 9) => (
        <div className={styles.fsCounter}>
            <button type="button" className={styles.fsCounterBtn} onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>−</button>
            <span className={styles.fsCounterVal}>{value}</span>
            <button type="button" className={styles.fsCounterBtn} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>+</button>
        </div>
    );

    const [seenPhases, setSeenPhases] = useState<SideBarRightProps['phase'][]>(initialSeenPhases ?? []);
    // Which sections are expanded — a set, not a single value, so the user
    // can have several open at once by hand. A *new* search still takes
    // the spotlight and collapses the rest (see the effect below); only
    // manual clicks are independent.
    const [openPhases, setOpenPhases] = useState<Set<SideBarRightProps['phase']>>(
        () => new Set(initialOpenPhase && initialOpenPhase !== 'idle' ? [initialOpenPhase] : [])
    );

    useEffect(() => {
        if (phase === 'idle') return;
        setSeenPhases((prev) => (prev.includes(phase) ? prev : [...prev, phase]));
        setOpenPhases(new Set([phase]));
    }, [phase]);

    const setFF = (key: keyof FlightFilters, val: unknown) =>
        onFlightFiltersChange({ ...flightFilters, [key]: val });

    const setHF = (key: keyof HotelFilters, val: unknown) =>
        onHotelFiltersChange({ ...hotelFilters, [key]: val });

    const setDF = (key: keyof DestinationFilters, val: unknown) =>
        onDestinationFiltersChange({ ...destinationFilters, [key]: val });

    const setRF = (key: keyof RoomFilters, val: unknown) =>
        onRoomFiltersChange({ ...roomFilters, [key]: val });

    const clearFlightChip = (key: string, val?: string | number) => {
        if (key === 'stops')    setFF('stops', 'any');
        else if (key === 'sort')     setFF('sort', 'best');
        else if (key === 'maxPrice') setFF('maxPrice', null);
        else if (key === 'time')     setFF('time', 'any');
        else if (key === 'airline')  setFF('airlines', flightFilters.airlines.filter(a => a !== val));
    };

    const clearHotelChip = (key: string, val?: string | number) => {
        if (key === 'star')         setHF('stars', hotelFilters.stars.filter(s => s !== val));
        else if (key === 'maxPrice')     setHF('maxPrice', null);
        else if (key === 'amenity')      setHF('amenities', hotelFilters.amenities.filter(a => a !== val));
        else if (key === 'propertyType') setHF('propertyType', hotelFilters.propertyType.filter(t => t !== val));
        else if (key === 'guestRating')  setHF('guestRating', 0);
    };

    const toggleList = <T,>(arr: T[], item: T): T[] =>
        arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];

    const hasFilterPanels = seenPhases.length > 0;
    const hasContent = phase !== 'idle' || selectedFlight || selectedHotel || hasFilterPanels;

    const phaseTitle = (phaseValue: SideBarRightProps['phase']) => {
        if (phaseValue === 'flights') return 'Flight filters';
        if (phaseValue === 'hotels') return 'Hotel filters';
        if (phaseValue === 'destinations') return 'Destination filters';
        return 'Room filters';
    };

    const renderFiltersByPhase = (phaseValue: SideBarRightProps['phase']) => {
        if (phaseValue === 'flights') {
            return (
                <div className={styles.fsFilters} key="flight-filters">
                    <ActiveChips
                        flightFilters={flightFilters}
                        hotelFilters={hotelFilters}
                        phase="flights"
                        onClearFlight={clearFlightChip}
                        onClearHotel={clearHotelChip}
                    />
                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Stops</span>
                        <div className={styles.fsPills}>
                            {([['any', 'Any'], ['direct', 'Direct'], ['1stop', '1 stop']] as [string, string][]).map(([val, label]) => (
                                <button key={val}
                                    className={`${styles.fsPill}${flightFilters.stops === val ? ` ${styles.active}` : ''}`}
                                    onClick={() => setFF('stops', val as FlightFilters['stops'])}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Sort</span>
                        <div className={styles.fsPills}>
                            {([['best', 'Best'], ['cheapest', 'Cheapest'], ['fastest', 'Fastest']] as [string, string][]).map(([val, label]) => (
                                <button key={val}
                                    className={`${styles.fsPill}${flightFilters.sort === val ? ` ${styles.active}` : ''}`}
                                    onClick={() => setFF('sort', val as FlightFilters['sort'])}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <div className={styles.fsLabelRow}>
                            <span className={styles.fsLabel}>Price</span>
                            <span className={styles.fsValue}>${flightFilters.maxPrice ?? maxFlightPrice}</span>
                        </div>
                        <div className={styles.fsRangeTrack}>
                            <input type="range" className={styles.fsRange}
                                min={0} max={Math.ceil(maxFlightPrice)} step={50}
                                value={flightFilters.maxPrice ?? maxFlightPrice}
                                onChange={e => setFF('maxPrice', +e.target.value)} />
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Departure time</span>
                        <div className={styles.fsPills}>
                            {([['any', 'Any'], ['morning', 'Morning'], ['afternoon', 'Afternoon'], ['evening', 'Evening']] as [string, string][]).map(([val, label]) => (
                                <button key={val}
                                    className={`${styles.fsPill}${flightFilters.time === val ? ` ${styles.active}` : ''}`}
                                    onClick={() => setFF('time', val as FlightFilters['time'])}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {airlines.length > 0 && (
                        <div className={styles.fsGroup}>
                            <span className={styles.fsLabel}>Airlines</span>
                            <div className={styles.fsPills}>
                                {airlines.map(code => (
                                    <button key={code}
                                        className={`${styles.fsPill}${flightFilters.airlines.includes(code) ? ` ${styles.active}` : ''}`}
                                        onClick={() => setFF('airlines',
                                            flightFilters.airlines.includes(code)
                                                ? flightFilters.airlines.filter(a => a !== code)
                                                : [...flightFilters.airlines, code]
                                        )}>
                                        {code}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (phaseValue === 'hotels') {
            return (
                <div className={styles.fsFilters} key="hotel-filters">
                    <ActiveChips
                        flightFilters={flightFilters}
                        hotelFilters={hotelFilters}
                        phase="hotels"
                        onClearFlight={clearFlightChip}
                        onClearHotel={clearHotelChip}
                    />
                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Star rating</span>
                        <div className={styles.fsPills}>
                            {([['any', 'Any'], [3, '3★'], [4, '4★'], [5, '5★']] as [string | number, string][]).map(([val, label]) => (
                                <button key={val}
                                    className={`${styles.fsPill}${
                                        val === 'any'
                                            ? hotelFilters.stars.length === 0 ? ` ${styles.active}` : ''
                                            : hotelFilters.stars.includes(val as number) ? ` ${styles.active}` : ''
                                    }`}
                                    onClick={() => {
                                        if (val === 'any') setHF('stars', []);
                                        else setHF('stars',
                                            hotelFilters.stars.includes(val as number)
                                                ? hotelFilters.stars.filter(s => s !== val)
                                                : [...hotelFilters.stars, val as number]
                                        );
                                    }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Amenities</span>
                        <div className={styles.fsPills}>
                            {['Pool', 'Breakfast', 'Free cancellation', 'WiFi'].map(a => (
                                <button key={a}
                                    className={`${styles.fsPill}${hotelFilters.amenities.includes(a) ? ` ${styles.active}` : ''}`}
                                    onClick={() => setHF('amenities',
                                        hotelFilters.amenities.includes(a)
                                            ? hotelFilters.amenities.filter(x => x !== a)
                                            : [...hotelFilters.amenities, a]
                                    )}>
                                    {a}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <div className={styles.fsLabelRow}>
                            <span className={styles.fsLabel}>Price per night</span>
                            <span className={styles.fsValue}>${hotelFilters.maxPrice ?? maxHotelPrice}</span>
                        </div>
                        <div className={styles.fsRangeTrack}>
                            <input type="range" className={styles.fsRange}
                                min={0} max={Math.ceil(maxHotelPrice)} step={25}
                                value={hotelFilters.maxPrice ?? maxHotelPrice}
                                onChange={e => setHF('maxPrice', +e.target.value)} />
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Property type</span>
                        <div className={styles.fsPills}>
                            {['Hotel', 'Apartment', 'Resort', 'Hostel'].map(t => (
                                <button key={t}
                                    className={`${styles.fsPill}${hotelFilters.propertyType.includes(t) ? ` ${styles.active}` : ''}`}
                                    onClick={() => setHF('propertyType',
                                        hotelFilters.propertyType.includes(t)
                                            ? hotelFilters.propertyType.filter(x => x !== t)
                                            : [...hotelFilters.propertyType, t]
                                    )}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <div className={styles.fsLabelRow}>
                            <span className={styles.fsLabel}>Guest rating</span>
                            <span className={styles.fsValue}>{hotelFilters.guestRating ?? 0}+</span>
                        </div>
                        <div className={styles.fsRangeTrack}>
                            <input type="range" className={styles.fsRange}
                                min={0} max={5} step={0.5}
                                value={hotelFilters.guestRating ?? 0}
                                onChange={e => setHF('guestRating', +e.target.value)} />
                        </div>
                    </div>
                </div>
            );
        }

        if (phaseValue === 'destinations') {
            return (
                <div className={styles.fsFilters} key="destination-filters">
                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Region</span>
                        <div className={styles.fsPills}>
                            {DESTINATION_REGIONS.map(r => (
                                <button key={r}
                                    className={`${styles.fsPill}${destinationFilters.regions.includes(r) ? ` ${styles.active}` : ''}`}
                                    onClick={() => setDF('regions', toggleList(destinationFilters.regions, r))}>
                                    {r}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Category</span>
                        <div className={styles.fsPills}>
                            {DESTINATION_CATEGORIES.map(c => (
                                <button key={c}
                                    className={`${styles.fsPill}${destinationFilters.categories.includes(c) ? ` ${styles.active}` : ''}`}
                                    onClick={() => setDF('categories', toggleList(destinationFilters.categories, c))}>
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <span className={styles.fsLabel}>Mood</span>
                        <div className={styles.fsPills}>
                            {DESTINATION_MOODS.map(m => (
                                <button key={m}
                                    className={`${styles.fsPill}${destinationFilters.moods.includes(m) ? ` ${styles.active}` : ''}`}
                                    onClick={() => setDF('moods', toggleList(destinationFilters.moods, m))}>
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fsGroup}>
                        <div className={styles.fsLabelRow}>
                            <span className={styles.fsLabel}>Max budget / trip</span>
                            <span className={styles.fsValue}>${destinationFilters.maxPrice ?? 3000}</span>
                        </div>
                        <div className={styles.fsRangeTrack}>
                            <input type="range" className={styles.fsRange}
                                min={0} max={3000} step={100}
                                value={destinationFilters.maxPrice ?? 3000}
                                onChange={e => setDF('maxPrice', +e.target.value)} />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.fsFilters} key="room-filters">
                <div className={styles.fsGroup}>
                    <span className={styles.fsLabel}>Bed type</span>
                    <div className={styles.fsPills}>
                        {ROOM_BED_TYPES.map(b => (
                            <button key={b}
                                className={`${styles.fsPill}${roomFilters.bedType === b ? ` ${styles.active}` : ''}`}
                                onClick={() => setRF('bedType', b)}>
                                {b}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.fsGroup}>
                    <span className={styles.fsLabel}>Amenities</span>
                    <div className={styles.fsPills}>
                        {ROOM_AMENITIES.map(a => (
                            <button key={a}
                                className={`${styles.fsPill}${roomFilters.amenities.includes(a) ? ` ${styles.active}` : ''}`}
                                onClick={() => setRF('amenities', toggleList(roomFilters.amenities, a))}>
                                {a}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.fsGroup}>
                    <div className={styles.fsLabelRow}>
                        <span className={styles.fsLabel}>Price per night</span>
                        <span className={styles.fsValue}>${roomFilters.maxPrice ?? 1000}</span>
                    </div>
                    <div className={styles.fsRangeTrack}>
                        <input type="range" className={styles.fsRange}
                            min={0} max={1000} step={25}
                            value={roomFilters.maxPrice ?? 1000}
                            onChange={e => setRF('maxPrice', +e.target.value)} />
                    </div>
                </div>
            </div>
        );
    };

    const panelClass = [
        styles.fsPanel,
        collapsed ? styles.collapsed : '',
        !hasContent ? styles.empty : '',
    ].filter(Boolean).join(' ');

    return (
        <aside className={panelClass}>
            {/* Header */}
            <div className={styles.fsHeader}>
                {!collapsed && (
                    <span className={styles.fsTitle}>
                        <SVGSlidersHorizontal width="12" height="12" fill="white" /> Filters
                    </span>
                )}
                <button className={styles.fsToggle} onClick={onToggle}>
                    {collapsed
                        ? <SVGChevronLeft width="16" height="16" />
                        : <SVGChevronRight width="16" height="16" />}
                </button>
            </div>

            {!collapsed && (
                <div className={styles.fsBody}>

                    {/*Statich filters with dropdown choice of Region, Budget, and Activity. These filters are always available regardless of the current phase.*/}

                    {/*<div className={styles.fsStaticFiltersSection}>*/}
                    {/*    <button*/}
                    {/*        type="button"*/}
                    {/*        className={styles.fsStaticFiltersHead}*/}
                    {/*        onClick={() => setFiltersOpen((f) => !f)}*/}
                    {/*    >*/}
                    {/*        <div className={styles.fsStaticFiltersTitleRow}>*/}
                    {/*            <SlidersHorizontal size={14} />*/}
                    {/*            <div className={styles.fsStaticFiltersTitle}>Filters</div>*/}
                    {/*        </div>*/}
                    {/*        <ChevronDown size={14} style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />*/}
                    {/*    </button>*/}
                    {/*    {filtersOpen && (*/}
                    {/*        <div className={styles.fsStaticFiltersPanel}>*/}
                    {/*            <div className={styles.fsStaticFilterGroup}>*/}
                    {/*                <label className={styles.fsStaticFilterLabel}>Region</label>*/}
                    {/*                <select className={styles.fsStaticFilterSelect} value={region} onChange={(e) => setRegion(e.target.value)}>*/}
                    {/*                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}*/}
                    {/*                </select>*/}
                    {/*            </div>*/}
                    {/*            <div className={styles.fsStaticFilterGroup}>*/}
                    {/*                <label className={styles.fsStaticFilterLabel}>Budget</label>*/}
                    {/*                <select className={styles.fsStaticFilterSelect} value={budget} onChange={(e) => setBudget(e.target.value)}>*/}
                    {/*                    {BUDGETS_LIST.map((b) => <option key={b} value={b}>{b}</option>)}*/}
                    {/*                </select>*/}
                    {/*            </div>*/}
                    {/*            <div className={styles.fsStaticFilterGroup}>*/}
                    {/*                <label className={styles.fsStaticFilterLabel}>Activity</label>*/}
                    {/*                <select className={styles.fsStaticFilterSelect} value={activity} onChange={(e) => setActivity(e.target.value)}>*/}
                    {/*                    {ACTIVITIES.map((a) => <option key={a} value={a}>{a}</option>)}*/}
                    {/*                </select>*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*</div>*/}
                    <div className={styles.fsStaticFilters}>
                        {/* ── Region row ── */}
                        <div className={styles.fsInfoRow}>
                            <div className={styles.fsInfoContent}>
                                <span className={styles.fsInfoLabel}>Region</span>
                                <span className={styles.fsInfoValue}>{region === 'All' ? 'Any region' : region}</span>
                            </div>
                            <button type="button" className={styles.fsInfoEdit} onClick={() => toggleEdit('region')} aria-label="Edit region">
                                <SVGEdit width="14" height="14" />
                            </button>
                        </div>
                        {editingField === 'region' && (
                            <div className={styles.fsInlineEditor}>
                                <div className={styles.fsStaticFilterGroup}>
                                    <label className={styles.fsStaticFilterLabel}>Region</label>
                                    <select className={styles.fsStaticFilterSelect} value={region} onChange={(e) => setRegion(e.target.value)}>
                                        {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                                <button type="button" className={styles.fsInlineSave} onClick={() => setEditingField(null)}>Done</button>
                            </div>
                        )}

                        {/* ── Check in / Check out row ── */}
                        <div className={styles.fsInfoRow}>
                            <div className={styles.fsInfoContent}>
                                <span className={styles.fsInfoLabel}>Check in &amp; Check out</span>
                                {checkIn && checkOut ? (
                                    <span className={`${styles.fsInfoValue} ${styles.fsInfoValueDatesClosed}`}>
                                        {checkIn}
                                        {nightsCount > 0 && <span className={styles.fsNightsBadge}>{nightsCount} nights</span>}
                                        {' '}{checkOut}
                                    </span>
                                ) : (
                                    <span className={styles.fsInfoPlaceholder}>Select dates</span>
                                )}
                            </div>
                            <button type="button" className={styles.fsInfoEdit} onClick={() => toggleEdit('dates')} aria-label="Edit dates">
                                <SVGEdit width="14" height="14" />
                            </button>
                        </div>
                        {editingField === 'dates' && (
                            <div className={styles.fsInlineEditor}>
                                <div className={styles.fsDateRow}>
                                    <div className={styles.fsStaticFilterGroup}>
                                        <label className={styles.fsStaticFilterLabel}>Check in</label>
                                        <input
                                            type="date"
                                            className={styles.fsDateInput}
                                            value={checkIn}
                                            onChange={e => updateDates(e.target.value, checkOut)}
                                        />
                                    </div>
                                    <div className={styles.fsStaticFilterGroup}>
                                        <label className={styles.fsStaticFilterLabel}>Check out</label>
                                        <input
                                            type="date"
                                            className={styles.fsDateInput}
                                            value={checkOut}
                                            min={checkIn || undefined}
                                            onChange={e => updateDates(checkIn, e.target.value)}
                                        />
                                    </div>
                                </div>
                                {nightsCount > 0 && <span className={styles.fsNightsBadge}>{nightsCount} nights</span>}
                                <button type="button" className={styles.fsInlineSave} onClick={() => setEditingField(null)}>Done</button>
                            </div>
                        )}

                        {/* ── Party size row ── */}
                        <div className={styles.fsInfoRow}>
                            <div className={styles.fsInfoContent}>
                                <span className={styles.fsInfoLabel}>Rooms and Guests</span>
                                <span className={styles.fsInfoValue}>{partySummary}</span>
                            </div>
                            <button type="button" className={styles.fsInfoEdit} onClick={() => toggleEdit('party')} aria-label="Edit party size">
                                <SVGEdit width="14" height="14" />
                            </button>
                        </div>
                        {editingField === 'party' && (
                            <div className={styles.fsInlineEditor}>
                                <div className={styles.fsPartyGroup}>
                                    <span className={styles.fsStaticFilterLabel}>Rooms</span>
                                    {counter(partyRooms, setPartyRooms, 1)}
                                </div>
                                <div className={styles.fsPartyGroup}>
                                    <span className={styles.fsStaticFilterLabel}>Adults</span>
                                    {counter(partyAdults, setPartyAdults, 1)}
                                </div>
                                <div className={styles.fsPartyGroup}>
                                    <span className={styles.fsStaticFilterLabel}>Children</span>
                                    {counter(partyChildren, setPartyChildren, 0)}
                                </div>
                                <button type="button" className={styles.fsInlineSave} onClick={() => setEditingField(null)}>Done</button>
                            </div>
                        )}
                    </div>
                    {/* Trip Builder Summary */}
                    {(selectedFlight || selectedHotel) && (
                        <div className={styles.fsTrip}>
                            <div className={styles.fsSectionLabel}>Your Trip</div>

                            {selectedFlight && (
                                <div className={styles.fsTripItem}>
                                    <SVGPlane width="13" height="13" />
                                    <div className={styles.fsTripInfo}>
                                        <span className={styles.fsTripRoute}>
                                            {selectedFlight.itineraries?.[0]?.segments?.[0]?.departure?.iataCode}
                                            {' → '}
                                            {selectedFlight.itineraries?.[0]?.segments?.slice(-1)[0]?.arrival?.iataCode}
                                        </span>
                                        <span className={styles.fsTripPrice}>
                                            {selectedFlight.price?.currency} {selectedFlight.price?.total}
                                        </span>
                                    </div>
                                    <button className={styles.fsTripRemove} onClick={onRemoveFlight}>
                                        <SVGCross width="12" height="12" />
                                    </button>
                                </div>
                            )}

                            {selectedHotel && (
                                <div className={styles.fsTripItem}>
                                    <SVGHotel width="13" height="13" />
                                    <div className={styles.fsTripInfo}>
                                        <span className={styles.fsTripRoute}>
                                            {selectedHotel.hotel?.name ?? 'Hotel'}
                                        </span>
                                        <span className={styles.fsTripPrice}>
                                            {selectedHotel.offers?.[0]?.price?.currency}{' '}
                                            {selectedHotel.offers?.[0]?.price?.total}
                                        </span>
                                    </div>
                                    <button className={styles.fsTripRemove} onClick={onRemoveHotel}>
                                        <SVGCross width="12" height="12" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Active phase filters are stacked into dropdowns and stay available.
                        Each one's open/closed state is independent — opening or closing
                        one by hand doesn't touch the others. */}
                    {seenPhases.map((phaseValue) => (
                        <Dropdown
                            key={`filter-${phaseValue}`}
                            title={phaseTitle(phaseValue)}
                            isOpen={openPhases.has(phaseValue)}
                            onToggle={(isOpen) => setOpenPhases((prev) => {
                                const next = new Set(prev);
                                if (isOpen) next.add(phaseValue); else next.delete(phaseValue);
                                return next;
                            })}
                        >
                            {renderFiltersByPhase(phaseValue)}
                        </Dropdown>
                    ))}

                    {/* Idle state */}
                    {phase === 'idle' && !selectedFlight && !selectedHotel && !hasFilterPanels && (
                        <div className={styles.fsEmpty}>
                            <SVGSlidersHorizontal width="12" height="12" strokeWidth={1.5} />
                            <p>Filters will appear here when you search for flights, hotels, destinations or rooms.</p>
                        </div>
                    )}

                    {/* Clear all */}
                    {hasFilterPanels && (
                        <button className={styles.fsClearAll} onClick={onClearAll}>
                            Clear all
                        </button>
                    )}
                </div>
            )}
        </aside>
    );
};
